import {
  ARCH_SURFACES,
  COMMON_SURFACE_COMBINATIONS,
  QUADRANT_SURFACES,
  SEXTANT_SURFACES,
  SINGLE_SURFACES,
} from "./surfaceSelectionConstants";
import { ToothSurface } from "../services/treatmentPlanService";
import { sanitizeFeeInput } from "./treatmentPlanDraftUtils";
import {
  allowsAreaSurfaceTab,
  allowsToothSurfaceTab,
  canSelectSurfaces,
  canSelectTeeth,
  EMPTY_TREAT_AREA_CAPABILITIES,
  hasValidToothCount,
  shouldMultiplyFeeAndDurationByAreaCount,
  shouldMultiplyFeeAndDurationByToothCount,
  type TreatAreaCapabilities,
} from "./treatAreaCapabilities";

const AREA_SURFACE_CODES = new Set<string>([
  ...QUADRANT_SURFACES.map((item) => item.code),
  ...SEXTANT_SURFACES,
  ...ARCH_SURFACES.map((item) => item.code),
]);

const TOOTH_SURFACE_CODES = new Set<string>([
  ...SINGLE_SURFACES,
  ...COMMON_SURFACE_COMBINATIONS,
]);

export function isAreaSurface(surf: string): boolean {
  return AREA_SURFACE_CODES.has(surf.toUpperCase());
}

export function isToothSurface(surf: string): boolean {
  const normalized = surf.toUpperCase();
  if (TOOTH_SURFACE_CODES.has(normalized)) return true;
  // Custom surface values that are not Q/S/A are treated as tooth surfaces
  return !isAreaSurface(normalized);
}

/** Quadrant / Sextant / Arch codes that never appear as tooth surfaces. */
function isPureAreaSurface(surf: string): boolean {
  const normalized = surf.toUpperCase();
  // "L" is both Lower Arch and Lingual — not pure area.
  if (TOOTH_SURFACE_CODES.has(normalized)) return false;
  return AREA_SURFACE_CODES.has(normalized);
}

export function getToothCount(teeth: string[]): number {
  return new Set(teeth.filter(Boolean)).size;
}

const QUAD_CODES = new Set<string>(QUADRANT_SURFACES.map((item) => item.code));
const SEXTANT_CODES = new Set<string>(SEXTANT_SURFACES);
const ARCH_CODES = new Set<string>(ARCH_SURFACES.map((item) => item.code));

/** Distinct selected quadrants or sextants (arches are excluded). */
export function getQuadOrSextantCount(surfaces: string[]): number {
  return new Set(
    surfaces
      .map((surf) => surf.toUpperCase())
      .filter(
        (surf) => QUAD_CODES.has(surf) || SEXTANT_CODES.has(surf),
      ),
  ).size;
}

export function getFeeMultiplier(
  teeth: string[],
  capabilities?: TreatAreaCapabilities,
  surfaces: string[] = [],
): number {
  const toothMultiply =
    !capabilities || shouldMultiplyFeeAndDurationByToothCount(capabilities);
  const areaMultiply =
    capabilities != null &&
    shouldMultiplyFeeAndDurationByAreaCount(capabilities);

  const areaCount = getQuadOrSextantCount(surfaces);

  // Prefer area count when Q/S areas are selected (replaces teeth for those codes).
  // Also scale when treatArea is missing from API flags but the provider already
  // selected quads/sextants on a non-tooth code (legacy needsSurface → all tabs).
  if (areaCount > 0 && (areaMultiply || !toothMultiply)) {
    return areaCount;
  }
  if (toothMultiply) {
    return Math.max(getToothCount(teeth), 1);
  }
  if (areaMultiply) {
    return Math.max(areaCount, 1);
  }
  return 1;
}

export function applyToothMultiplier(
  base: number,
  teeth: string[],
  capabilities?: TreatAreaCapabilities,
  surfaces: string[] = [],
): number {
  return base * getFeeMultiplier(teeth, capabilities, surfaces);
}

export function scaleFeeAndDurationForTeeth(params: {
  fee: string;
  durationMinutes: number;
  previousTeeth: string[];
  nextTeeth: string[];
  previousSurfaces?: string[];
  nextSurfaces?: string[];
  isFeeEdited: boolean;
  capabilities?: TreatAreaCapabilities;
}): { fee: string; durationMinutes: number } {
  const previousMultiplier = getFeeMultiplier(
    params.previousTeeth,
    params.capabilities,
    params.previousSurfaces ?? [],
  );
  const nextMultiplier = getFeeMultiplier(
    params.nextTeeth,
    params.capabilities,
    params.nextSurfaces ?? [],
  );

  if (previousMultiplier === nextMultiplier) {
    return {
      fee: params.fee,
      durationMinutes: params.durationMinutes,
    };
  }

  const baseDuration = params.durationMinutes / previousMultiplier;
  const nextDuration = Math.round(baseDuration * nextMultiplier);

  if (params.isFeeEdited) {
    return {
      fee: params.fee,
      durationMinutes: nextDuration,
    };
  }

  const baseFee = (parseFloat(params.fee) || 0) / previousMultiplier;
  return {
    fee: sanitizeFeeInput(String(Math.round(baseFee * nextMultiplier))),
    durationMinutes: nextDuration,
  };
}

export function hasAreaSelection(surfaces: string[]): boolean {
  // Includes Quadrant / Sextant / Arch (U, L). "L" is ambiguous with Lingual;
  // callers that must not treat Lingual as area should use isPureAreaSurface.
  return surfaces.some((surf) => AREA_SURFACE_CODES.has(surf.toUpperCase()));
}

export function hasPureAreaSelection(surfaces: string[]): boolean {
  return surfaces.some((surf) => isPureAreaSurface(surf));
}

export function hasToothSurfaceSelection(surfaces: string[]): boolean {
  return surfaces.some((surf) => isToothSurface(surf));
}

/** Complete treat area relative to CDT treatArea / needs* capabilities. */
export function hasCompleteTreatArea(
  teeth: string[],
  surfaces: string[],
  capabilities?: TreatAreaCapabilities,
): boolean {
  const caps = capabilities ?? {
    ...EMPTY_TREAT_AREA_CAPABILITIES,
    needsTooth: true,
    needsSurface: true,
    allowedSurfaceTabs: ["surface", "quad", "sextant", "arch"],
  };
  const needsAnyTooth = caps.needsTooth || caps.needsRange;
  const needsSurf = canSelectSurfaces(caps);

  if (!needsAnyTooth && !needsSurf) {
    return true;
  }

  // Q/S/A satisfies surface requirement only when those tabs are allowed.
  if (hasAreaSelection(surfaces) && allowsAreaSurfaceTab(caps)) {
    return true;
  }

  const teethOk = hasValidToothCount(teeth, caps);
  const hasSurf = hasToothSurfaceSelection(surfaces);

  if (needsAnyTooth && needsSurf) {
    if (!allowsToothSurfaceTab(caps)) {
      // Surface required only via area tabs — teeth alone are not enough.
      return false;
    }
    return teethOk && hasSurf;
  }
  if (needsAnyTooth) {
    return teethOk;
  }
  return allowsToothSurfaceTab(caps) ? hasSurf : false;
}

export function hasMissingTooth(
  teeth: string[],
  surfaces: string[],
  capabilities?: TreatAreaCapabilities,
): boolean {
  const caps = capabilities ?? {
    ...EMPTY_TREAT_AREA_CAPABILITIES,
    needsTooth: true,
    needsSurface: true,
    allowedSurfaceTabs: ["surface", "quad", "sextant", "arch"],
  };
  if (!caps.needsTooth && !caps.needsRange) return false;
  // Area selection replaces teeth when area tabs are allowed.
  if (hasAreaSelection(surfaces) && allowsAreaSurfaceTab(caps)) return false;
  return !hasValidToothCount(teeth, caps);
}

export function hasMissingSurface(
  teeth: string[],
  surfaces: string[],
  capabilities?: TreatAreaCapabilities,
): boolean {
  const caps = capabilities ?? {
    ...EMPTY_TREAT_AREA_CAPABILITIES,
    needsTooth: true,
    needsSurface: true,
    allowedSurfaceTabs: ["surface", "quad", "sextant", "arch"],
  };
  if (!canSelectSurfaces(caps)) return false;
  if (hasAreaSelection(surfaces) && allowsAreaSurfaceTab(caps)) return false;
  if (!allowsToothSurfaceTab(caps)) {
    // Only Q/S/A allowed — missing until an area code is chosen.
    return true;
  }
  return !hasToothSurfaceSelection(surfaces);
}

export function getToothSurfaceValues(surfaces: string[]): string[] {
  return surfaces.filter((surf) => isToothSurface(surf));
}

export function getAreaSurfaceValues(surfaces: string[]): string[] {
  return surfaces.filter((surf) => AREA_SURFACE_CODES.has(surf.toUpperCase()));
}

/** Keep only surface values allowed by current treatArea capabilities. */
export function filterSurfacesForCapabilities(
  surfaces: string[],
  capabilities: TreatAreaCapabilities,
): string[] {
  if (!canSelectSurfaces(capabilities)) return [];

  return surfaces.filter((surf) => {
    const normalized = surf.toUpperCase();
    if (QUAD_CODES.has(normalized)) {
      return capabilities.allowedSurfaceTabs.includes("quad");
    }
    if (SEXTANT_CODES.has(normalized)) {
      return capabilities.allowedSurfaceTabs.includes("sextant");
    }
    if (ARCH_CODES.has(normalized)) {
      return capabilities.allowedSurfaceTabs.includes("arch");
    }
    // Tooth / custom surfaces
    return capabilities.allowedSurfaceTabs.includes("surface");
  });
}

/** Clear Q/S/A (pure area codes) when switching to Tooth+Surface shape. */
export function clearAreaSurfaces(surfaces: string[]): string[] {
  return surfaces.filter((surf) => !isPureAreaSurface(surf));
}

/** Keep Q/S/A values when switching to whole-area shape (includes arch U/L). */
export function keepOnlyAreaSurfaces(surfaces: string[]): string[] {
  return getAreaSurfaceValues(surfaces);
}

/**
 * Keep only one area family (quads OR sextants OR arches) so providers
 * cannot mix types on a single step.
 */
export function keepOnlyMatchingAreaFamily(
  surfaces: string[],
  selectedCode: string,
): string[] {
  const normalized = selectedCode.toUpperCase();
  const areaOnly = keepOnlyAreaSurfaces(surfaces);

  if (QUAD_CODES.has(normalized)) {
    return areaOnly.filter((surf) => QUAD_CODES.has(surf.toUpperCase()));
  }
  if (SEXTANT_CODES.has(normalized)) {
    return areaOnly.filter((surf) => SEXTANT_CODES.has(surf.toUpperCase()));
  }
  if (ARCH_CODES.has(normalized)) {
    return areaOnly.filter((surf) => ARCH_CODES.has(surf.toUpperCase()));
  }
  return areaOnly;
}

function normalizeSurfacesList(surfaces: unknown): string[] {
  if (Array.isArray(surfaces)) {
    return surfaces.filter((item): item is string => typeof item === "string");
  }
  if (
    surfaces &&
    typeof surfaces === "object" &&
    Array.isArray((surfaces as { surfaces?: unknown }).surfaces)
  ) {
    return ((surfaces as { surfaces: unknown[] }).surfaces).filter(
      (item): item is string => typeof item === "string",
    );
  }
  return [];
}

function expandToothRange(toothRange: string): string[] {
  const parts = toothRange
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);

  const teeth: string[] = [];

  for (const part of parts) {
    if (part.includes("-")) {
      const [fromRaw, toRaw] = part.split("-").map((item) => item.trim());
      const from = Number.parseInt(fromRaw, 10);
      const to = Number.parseInt(toRaw, 10);
      if (Number.isNaN(from) || Number.isNaN(to)) {
        teeth.push(part);
        continue;
      }
      const start = Math.min(from, to);
      const end = Math.max(from, to);
      for (let tooth = start; tooth <= end; tooth += 1) {
        teeth.push(String(tooth));
      }
      continue;
    }
    teeth.push(part);
  }

  return teeth;
}

/**
 * Tooth range string for the ToothRange treat area: comma separated numbers
 * and/or hyphen separated spans, per the Open Dental ToothRange format.
 */
function buildToothRangeValue(teeth: string[]): string | null {
  const unique = Array.from(new Set(teeth.filter(Boolean)));
  if (unique.length === 0) return null;
  if (unique.length === 1) return unique[0];

  const numericTeeth = unique.map((tooth) => {
    const parsed = Number.parseInt(tooth, 10);
    return Number.isNaN(parsed) || String(parsed) !== tooth ? null : parsed;
  });

  if (numericTeeth.every((tooth): tooth is number => tooth !== null)) {
    const sorted = [...numericTeeth].sort((a, b) => a - b);
    const isConsecutive = sorted.every(
      (tooth, index) => index === 0 || tooth === sorted[index - 1] + 1,
    );
    if (isConsecutive) {
      return `${sorted[0]}-${sorted[sorted.length - 1]}`;
    }
    return sorted.join(",");
  }

  return unique.join(",");
}

/**
 * All selected tooth surfaces concatenated into one Surf value ("M" + "O" → "MO").
 * Open Dental stores multi-surface restorations as a single ProcedureLog row,
 * so surfaces are never split across entries. Normalization of the resulting
 * string (aliases, duplicates, ordering) is done by the backend.
 */
function joinToothSurfaces(surfaces: string[]): string | null {
  const joined = getToothSurfaceValues(surfaces).join("");
  return joined || null;
}

/**
 * One entry per tooth. Permanent teeth are numeric and use toothNum; primary
 * teeth are letters (A-T) which toothNum cannot hold, so they fall back to
 * toothRange carrying the single letter.
 */
function buildToothEntry(tooth: string, surf: string | null): ToothSurface {
  const asNumber = Number.parseInt(tooth, 10);
  if (!Number.isNaN(asNumber) && String(asNumber) === tooth) {
    return { surf, toothNum: asNumber, toothRange: null };
  }
  return { surf, toothNum: null, toothRange: tooth };
}

export function parseToothSurfaces(
  items: ToothSurface[] | null | undefined,
): { teeth: string[]; surfaces: string[] } {
  if (!items || items.length === 0) {
    return { teeth: [], surfaces: [] };
  }

  const teethSet = new Set<string>();
  const surfacesSet = new Set<string>();

  items.forEach((item) => {
    if (item.surf) {
      surfacesSet.add(item.surf);
    }

    if (item.toothNum != null) {
      teethSet.add(String(item.toothNum));
    }

    if (item.toothRange) {
      expandToothRange(item.toothRange).forEach((tooth) => teethSet.add(tooth));
    }
  });

  return {
    teeth: Array.from(teethSet),
    surfaces: Array.from(surfacesSet),
  };
}

/**
 * Build API toothSurfaces from UI teeth/surfaces, shaped by the step's treat
 * area so the backend can map each entry onto an Open Dental ProcedureLog row:
 *
 * - None / Mouth          → no entries
 * - Tooth                 → one entry per tooth, toothNum only
 * - Surf                  → one entry per tooth, toothNum + concatenated surf
 * - Quad / Sextant / Arch → one entry per area code, no teeth
 * - ToothRange            → a single entry carrying the whole range
 *
 * Without capabilities the Tooth/Surf shape is assumed, which is the safe
 * default for every treat area except ToothRange.
 */
export function buildToothSurfaces(
  teeth: string[],
  surfaces: unknown,
  capabilities?: TreatAreaCapabilities,
): ToothSurface[] {
  const surfaceList = normalizeSurfacesList(surfaces);

  // None / Mouth treat areas carry neither teeth nor surfaces.
  if (
    capabilities &&
    !canSelectTeeth(capabilities) &&
    !canSelectSurfaces(capabilities)
  ) {
    return [];
  }

  const areaSurfaces = getAreaSurfaceValues(surfaceList);
  const onlyArea =
    areaSurfaces.length > 0 && areaSurfaces.length === surfaceList.length;
  // "L" is both Lower Arch and Lingual — capabilities disambiguate the two.
  const isAreaSelection =
    onlyArea &&
    (!capabilities ||
      (allowsAreaSurfaceTab(capabilities) &&
        (hasPureAreaSelection(surfaceList) ||
          !allowsToothSurfaceTab(capabilities))));

  // Whole-area selection must never carry toothNum/toothRange — even if the
  // draft still has stale teeth from a previous treat-area mode.
  if (isAreaSelection) {
    return areaSurfaces.map((surf) => ({
      surf,
      toothNum: null,
      toothRange: null,
    }));
  }

  const uniqueTeeth = Array.from(new Set(teeth.filter(Boolean)));
  const surf = joinToothSurfaces(surfaceList);

  // ToothRange treat area — the whole span is one procedure, not one per tooth.
  if (capabilities && capabilities.needsRange && !capabilities.needsTooth) {
    if (!uniqueTeeth.length) {
      return surf ? [{ surf, toothNum: null, toothRange: null }] : [];
    }
    return [
      { surf, toothNum: null, toothRange: buildToothRangeValue(uniqueTeeth) },
    ];
  }

  // Tooth / Surf treat areas — one entry per tooth, surfaces concatenated.
  if (uniqueTeeth.length) {
    return uniqueTeeth.map((tooth) => buildToothEntry(tooth, surf));
  }

  // Surface picked before any tooth — keep the incomplete draft selection.
  return surf ? [{ surf, toothNum: null, toothRange: null }] : [];
}
